import React, { useState, useEffect } from 'react';
import {
  Badge, IconButton, Popover, Box, Typography, Chip, Button, Divider,
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import MentionsList, { useMentions, jumpPath } from './social/MentionsInbox';
import { NOTIFICATIONS_CHANGED, routeForNotification } from './notificationRoutes';
import { notificationsFeed } from './notificationsFeed';
import { usePoller } from '../hooks/sharedPoll';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/*
  NotificationBell — drop into your top bar / header:
      import NotificationBell from './components/NotificationBell';
      ...
      <NotificationBell />
  Polls unread count every 60s and on window focus. Click = dropdown of latest 8,
  click an item = mark read, "View all" = /app/notifications.

  dark: optional. true forces the dark palette (arena's forced-dark pages,
  claude_code_backend_gaps.md item 10); left out, the dropdown follows the
  app's light / dark setting (MUI useColorScheme, "system" resolved).
*/
/** Severity chip colour, shared with the pop-ups (NotificationPopups.jsx). */
export const severityColor = (s) => (s === 'critical' ? '#dc2626' : s === 'warning' ? '#d97706' : '#6b7280');

// Two hardcoded palettes, picked by the dark flag - not a theme read/write, just which fixed set of colors the
// dropdown (and the pop-ups, so they look the same) render with.
export const bellPalette = (dark) => (dark
  ? { paperBg: '#16171b', headerText: '#f2f3f5', divider: '#2a2c33', emptyText: '#8b9099',
      unreadBg: '#1c2230', readBg: '#16171b', unreadHover: '#232b3d', readHover: '#1c1d22',
      border: '#2a2c33', titleText: '#f2f3f5', bodyText: '#9aa0aa', timeText: '#666a73' }
  : { paperBg: '#fff', headerText: '#111827', divider: undefined, emptyText: '#9ca3af',
      unreadBg: '#f0f7ff', readBg: '#fff', unreadHover: '#e3f0fd', readHover: '#fafafa',
      border: '#f3f4f6', titleText: '#111827', bodyText: '#6b7280', timeText: '#b0b7c3' });

export default function NotificationBell({ dark: forceDark }) {
  const { mode, systemMode } = useColorScheme();
  const dark = forceDark ?? ((mode === 'system' ? systemMode : mode) === 'dark');
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  const feed = usePoller(notificationsFeed);
  const [readIds, setReadIds] = useState(() => new Set()); // read here, before the next poll catches up
  const items = (feed || []).slice(0, 8).map((n) => (readIds.has(n.notification_id) || readIds.has('all') ? { ...n, is_read: true } : n));
  const mentions = useMentions();
  const [panel, setPanel] = useState('all');
  const unread = items.filter(n => !n.is_read).length + mentions.unread;
  const load = () => notificationsFeed.refresh();

  useEffect(() => setReadIds(new Set()), [feed]); // the server's copy has caught up

  useEffect(() => {
    const onChanged = () => notificationsFeed.refresh(); // a pop-up was opened or read
    window.addEventListener(NOTIFICATIONS_CHANGED, onChanged);
    return () => window.removeEventListener(NOTIFICATIONS_CHANGED, onChanged);
  }, []);

  const markRead = (n) => {
    if (n.is_read) return;
    setReadIds((prev) => new Set(prev).add(n.notification_id));
    fetch(`${API_BASE}/api/notifications/${n.notification_id}/read`,
      { method: 'PUT', headers: authHeaders() }).then(load);
  };

  const openMention = (m) => {
    if (!m.read) mentions.markRead([m.id]);
    const path = jumpPath(m.url);
    if (path) { setAnchor(null); navigate(path); }
  };

  const markAllRead = () => {
    if (mentions.available && panel === 'mentions') { mentions.markRead('all'); return; }
    if (mentions.unread > 0) mentions.markRead('all');
    setReadIds(new Set(['all']));
    fetch(`${API_BASE}/api/notifications/read-all`,
      { method: 'PUT', headers: authHeaders() }).then(load);
  };


  const handleItemClick = (n) => {
    markRead(n);
    const route = routeForNotification(n);
    if (route) {
      setAnchor(null);
      navigate(route);
    }
  };

  const pal = bellPalette(dark);
  const sevColor = severityColor;

  return (
    <>
      <IconButton onClick={e => { setAnchor(e.currentTarget); load(); }} size="small">
        <Badge badgeContent={unread} color="error" max={9}>
          <NotificationsIcon sx={dark ? { color: pal.headerText } : undefined} />
        </Badge>
      </IconButton>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxHeight: 440, bgcolor: pal.paperBg } }}>
        <Box sx={{ p: 1.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: pal.headerText }}>Notifications</Typography>
          {/* Always there; greyed out with nothing unread (it used to vanish, which read as missing). */}
          <Button size="small" onClick={markAllRead} disabled={unread === 0}
            sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0, p: 0, color: '#0A84FF', '&.Mui-disabled': { color: pal.timeText } }}>
            Mark all read
          </Button>
        </Box>
        <Divider sx={dark ? { borderColor: pal.divider } : undefined} />
        {mentions.available && (
          <Box role="tablist" aria-label="Notification type" sx={{ display: 'flex', gap: 0.5, px: 1.5, py: 0.75, borderBottom: `1px solid ${pal.border}` }}>
            {[['all', 'All'], ['mentions', mentions.unread ? `Mentions (${mentions.unread})` : 'Mentions']].map(([k, label]) => (
              <Box key={k} component="button" type="button" role="tab" aria-selected={panel === k} onClick={() => setPanel(k)}
                sx={{ border: 0, cursor: 'pointer', font: 'inherit', fontSize: '0.74rem', fontWeight: 600, px: 1.25, py: 0.4, borderRadius: 999,
                  bgcolor: panel === k ? (dark ? '#3a3a3c' : '#e9e9ec') : 'transparent', color: panel === k ? pal.titleText : pal.bodyText,
                  '&:focus-visible': { outline: '2px solid #0A84FF' } }}>{label}</Box>
            ))}
          </Box>
        )}
        {mentions.available && panel === 'mentions' ? (
          <>
            <MentionsList mentions={mentions.items} onOpen={openMention} pal={pal} />
            {mentions.next && (
              <Box sx={{ p: 1, textAlign: 'center' }}>
                <Button size="small" onClick={mentions.loadMore} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Load more</Button>
              </Box>
            )}
          </>
        ) : items.length === 0 ? (
          <Typography sx={{ p: 2, color: pal.emptyText, fontSize: '0.82rem', textAlign: 'center' }}>
            Nothing yet — create alerts in the 🔔 Notifications page.
          </Typography>
        ) : items.map(n => (
          <Box key={n.notification_id} onClick={() => handleItemClick(n)}
            sx={{ px: 1.5, py: 1, cursor: routeForNotification(n) ? 'pointer' : 'default',
              bgcolor: n.is_read ? pal.readBg : pal.unreadBg, borderBottom: `1px solid ${pal.border}`,
              '&:hover': { bgcolor: n.is_read ? pal.readHover : pal.unreadHover } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontWeight: n.is_read ? 500 : 700, fontSize: '0.8rem', color: pal.titleText }}>
                {n.title}
              </Typography>
              <Chip size="small" label={n.severity}
                sx={{ height: 16, fontSize: '0.58rem', color: sevColor(n.severity),
                      bgcolor: `${sevColor(n.severity)}18` }} />
            </Box>
            <Typography sx={{ fontSize: '0.72rem', color: pal.bodyText,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {n.message}
            </Typography>
            <Typography sx={{ fontSize: '0.62rem', color: pal.timeText }}>
              {new Date(n.created_at).toLocaleString()}
            </Typography>
          </Box>
        ))}
        {!(mentions.available && panel === 'mentions') && <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button size="small" onClick={() => { setAnchor(null); navigate('/app/notifications'); }}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
            View all →
          </Button>
        </Box>}
      </Popover>
    </>
  );
}
